import { useState } from "react";
import { Button, Select, SelectField, TextField } from "@/components/ui/FormControls";
import { api } from "@/lib/api";

export interface WorkspaceMember {
  id: string;
  role: "owner" | "editor" | "reviewer" | "viewer";
  createdAt: string;
  user: { id: string; email: string; name: string; organization: string };
}

interface WorkspaceMembersPanelProps {
  members: WorkspaceMember[];
  onChanged: () => void;
}

const ROLE_LABEL: Record<WorkspaceMember["role"], string> = { owner: "소유자", editor: "편집자", reviewer: "검토자", viewer: "조회자" };

export function WorkspaceMembersPanel({ members, onChanged }: WorkspaceMembersPanelProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WorkspaceMember["role"]>("viewer");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      await api("/workspace/members", { method: "POST", json: { email, role } });
      setEmail("");
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "구성원을 추가하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(member: WorkspaceMember, nextRole: string) {
    try {
      await api(`/workspace/members/${member.id}`, { method: "PATCH", json: { role: nextRole } });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "구성원 권한을 바꾸지 못했습니다.");
    }
  }

  async function remove(member: WorkspaceMember) {
    if (!window.confirm(`${member.user.name} 구성원을 작업공간에서 제거할까요?`)) return;
    try {
      await api(`/workspace/members/${member.id}`, { method: "DELETE" });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "구성원을 제거하지 못했습니다.");
    }
  }

  return (
    <section className="workspace-card">
      <div className="workspace-card-head"><div><h2 className="workspace-card-title">작업공간 구성원 <span className="workspace-count">{members.length}명</span></h2><p className="workspace-card-desc">가입된 계정을 추가하고 프로젝트 자료를 볼 수 있는 권한을 나눕니다.</p></div></div>
      <div className="workspace-member-list">{members.map((member) => <div className="workspace-member-row" key={member.id}><div><strong>{member.user.name}</strong><span>{member.user.email} | {member.user.organization}</span></div><div className="workspace-actions"><div className="workspace-role-select"><Select aria-label={`${member.user.name} 권한`} value={member.role} onChange={(event) => void changeRole(member, event.target.value)} disabled={member.role === "owner"}><option value="owner">{ROLE_LABEL.owner}</option><option value="editor">{ROLE_LABEL.editor}</option><option value="reviewer">{ROLE_LABEL.reviewer}</option><option value="viewer">{ROLE_LABEL.viewer}</option></Select></div>{member.role !== "owner" && <Button variant="danger" onClick={() => void remove(member)}>제거</Button>}</div></div>)}</div>
      <div className="workspace-form-grid workspace-member-form"><TextField label="가입된 계정 이메일" type="email" placeholder="member@company.com" value={email} onChange={(event) => setEmail(event.target.value)} /><SelectField label="초기 권한" value={role} onChange={(event) => setRole(event.target.value as WorkspaceMember["role"])}><option value="editor">{ROLE_LABEL.editor}</option><option value="reviewer">{ROLE_LABEL.reviewer}</option><option value="viewer">{ROLE_LABEL.viewer}</option></SelectField><div className="workspace-actions workspace-actions-end"><Button variant="primary" disabled={busy || !email.trim()} onClick={() => void add()}>{busy ? "추가 중" : "구성원 추가"}</Button></div></div>
      {error && <p className="note note-danger text-caption" role="alert">{error}</p>}
    </section>
  );
}
